import { Form, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useMediaQuery, useTheme, Grid } from '@mui/material'

import {
  FormSubmitButton,
  FormTitle,
  TextInput,
  PhoneInput,
  FormImage,
  HelperWrapper,
  UserWalletInput,
} from 'src/components'
import { useMenuItems, usePrompt } from 'src/hooks'

import { ETranslationNs, IProfileUserForm, IUserWalletBase } from 'src/types'

import { useStyles } from '../styles'

interface IProfileFormProps {
  isLoading: boolean
}

const ProfileForm = ({ isLoading }: IProfileFormProps) => {
  const [t] = useTranslation([
    ETranslationNs.Form,
    ETranslationNs.UserProfile,
    ETranslationNs.Navbar,
  ])
  const classes = useStyles()
  const { values, handleChange, errors, setFieldValue, isSubmitting, dirty } =
    useFormikContext<IProfileUserForm>()
  const theme = useTheme()
  usePrompt({
    message: isLoading ? t('prompt-loading-changes.v') : t('prompt-unsaved-changes.v'),
    when: (dirty && !isSubmitting) || isLoading,
    beforeUnload: true,
  })

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const { handleChooseMenuItem, menuItems } = useMenuItems([
    {
      title: t('user-profile-header-title.v', { ns: ETranslationNs.Navbar }),
      isActive: true,
      tab: 'profile',
    },
  ])

  const handlePhoneChange = (v: string) => {
    if (v === '') {
      setFieldValue('mobile', null)
      return
    }

    setFieldValue('mobile', v)
  }

  return (
    <>
      <Form>
        {!isTablet && (
          <FormTitle
            imageComponent={<FormImage alt={values.name} />}
            menuItems={menuItems}
            handleChooseMenuItem={handleChooseMenuItem}
            setFieldValue={setFieldValue}
            title={values.name}
          />
        )}
        <Grid className={classes.form}>
          <Grid className={classes.formWrapper} container>
            {isTablet && (
              <Grid className={classes.mainImageWrapper}>
                <FormImage alt={values.name} />
              </Grid>
            )}
            <div className={classes.inputsRowWrapper}>
              <TextInput
                inputName="name"
                handleChange={handleChange}
                value={values.name}
                error={errors.name}
                inputPlaceholder="John Doe"
                inputUpperLabel={t('name-input-label.v')}
              />
              <TextInput
                inputName="email"
                handleChange={handleChange}
                value={values.email}
                inputPlaceholder="example@mail.com"
                inputUpperLabel={t('email-input-label.v')}
                disabled
              />
            </div>
            <div className={classes.inputsRowWrapper}>
              <PhoneInput
                inputName="mobile"
                handleChange={handlePhoneChange}
                value={values.mobile}
                error={errors.mobile}
                inputPlaceholder={t('account-form-mobile-input-placeholder.v', {
                  ns: ETranslationNs.Form,
                })}
                inputUpperLabel={
                  <HelperWrapper
                    t="account-form-mobile-input-label"
                    ns={ETranslationNs.Form}
                    isColorInherited
                  />
                }
              />
              <UserWalletInput withLink />
            </div>
          </Grid>
          <FormSubmitButton
            validText={t('save-changes-button-text.v', { ns: ETranslationNs.Form })}
            invalidText={t('user-profile-form-submit.v', { ns: ETranslationNs.UserProfile })}
          />
        </Grid>
      </Form>
    </>
  )
}

export { ProfileForm }
